import { useCallback, useMemo } from 'react'
import { ReferencedEntity } from '../../../../domain/value-object/ReferencedEntity'
import { WbsItemFormModel } from '../model'
import { StatusAndSubStatusRelation } from './wbsItemSelectOptions'
import { WbsItemAdditionalPropertyValueType } from '../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { ValidateSingleSheetPropertyValue } from '../../../containers/SingleSheetV2/hooks/formValidation'
import { FormModelModifiers } from '../../../containers/SingleSheetV2/model/formModelModifiers'
import { WbsItemAdditionalPropertyLayoutEntity } from '../../../../domain/entity/WbsItemAdditionalPropertyLayoutEntity'
import { WbsItemAdditionalPropertyEntity } from '../../../../domain/entity/WbsItemAdditionalPropertyEntity'

export const useWbsItemModelModifier = (
  onChange: <K extends keyof WbsItemFormModel, T extends WbsItemFormModel[K]>(
    path: K,
    value: T
  ) => void,
  _onChangeAdditionalPropertyValue: (
    wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
    value: WbsItemAdditionalPropertyValueType
  ) => void,
  { getStatusBySubStatus, getSubStatusByStatus }: StatusAndSubStatusRelation,
  wbsItemAdditionalProperties:
    | WbsItemAdditionalPropertyLayoutEntity
    | undefined,
  validate: ValidateSingleSheetPropertyValue,
  isTaskSprintRuleSingle?: boolean
) => {
  const onChangeDisplayName = useCallback(
    (value: WbsItemFormModel['displayName']) => {
      onChange('displayName', value)
      validate('displayName', value)
    },
    [onChange, validate]
  )
  const onChangeStatus = useCallback(
    (value: WbsItemFormModel['status']) => {
      onChange('status', value)
      validate('status', value)
      const substatus = getSubStatusByStatus(value)?.value
      onChange('substatus', substatus)
      validate('substatus', substatus)
    },
    [getSubStatusByStatus, onChange, validate]
  )
  const onChangeSubStatus = useCallback(
    (value: WbsItemFormModel['substatus']) => {
      onChange('substatus', value)
      validate('substatus', value)
      const status = getStatusBySubStatus(value)
      onChange('status', status)
      validate('substatus', status)
    },
    [getStatusBySubStatus, onChange, validate]
  )
  const onChangeTags = useCallback(
    (value: WbsItemFormModel['tags']) => {
      onChange('tags', value)
      validate('tags', value)
    },
    [onChange, validate]
  )
  const onChangeTeam = useCallback(
    (value: WbsItemFormModel['team']) => {
      onChange('team', value)
      validate('team', value)
      onChange('sprint', undefined)
      onChange('sprints', undefined)
    },
    [onChange, validate]
  )
  const onChangeAccountable = useCallback(
    (value: WbsItemFormModel['accountable']) => {
      onChange('accountable', value)
      validate('accountable', value)
    },
    [onChange, validate]
  )
  const onChangeResponsible = useCallback(
    (value: WbsItemFormModel['responsible']) => {
      onChange('responsible', value)
      validate('responsible', value)
    },
    [onChange, validate]
  )
  const onChangeAssignee = useCallback(
    (value: WbsItemFormModel['assignee']) => {
      onChange('assignee', value)
      validate('assignee', value)
    },
    [onChange, validate]
  )
  const onChangeWatchers = useCallback(
    (value: WbsItemFormModel['watchers']) => {
      onChange('watchers', value)
      validate('watchers', value)
    },
    [onChange, validate]
  )
  const onChangeDifficulty = useCallback(
    (value: WbsItemFormModel['difficulty']) => {
      onChange('difficulty', value)
      validate('difficulty', value)
    },
    [onChange, validate]
  )
  const onChangePriority = useCallback(
    (value: WbsItemFormModel['priority']) => {
      onChange('priority', value)
      validate('priority', value)
    },
    [onChange, validate]
  )
  const onChangeEstimatedStoryPoint = useCallback(
    (value: WbsItemFormModel['estimatedStoryPoint']) => {
      onChange('estimatedStoryPoint', value)
      validate('estimatedStoryPoint', value)
    },
    [onChange, validate]
  )
  const onChangeEstimatedHour = useCallback(
    (value: WbsItemFormModel['estimatedHour']) => {
      onChange('estimatedHour', value)
      validate('estimatedHour', value)
    },
    [onChange, validate]
  )
  const onChangeSprint = useCallback(
    (value: WbsItemFormModel['sprint']) => {
      onChange('sprint', value)
      validate('sprint', value)
    },
    [onChange, validate]
  )
  const onChangeSprints = useCallback(
    (value: WbsItemFormModel['sprints']) => {
      // MUIはチップの並びを value の並びとして扱う（キーボード移動やBackspaceの対象が
      // data-tag-index で決まる）ため、表示順に並べ替えるのではなく value 自体を並べる。
      // 描画のたびに並べ替えると value の参照が変わり、MUIが入力中の絞り込み文字を消してしまうため、
      // formModel に入れる時点で並べる（API応答からの変換も同じ順に並べている）。
      const sprints = value?.toSorted((a, b) => a.num - b.num)
      onChange('sprints', sprints)
      validate('sprints', sprints)
    },
    [onChange, validate]
  )
  const onChangeSprintSingleFromSprints = useCallback(
    (value: ReferencedEntity | undefined) => {
      const sprints = value ? [value] : []
      onChange('sprints', sprints as WbsItemFormModel['sprints'])
      validate('sprints', sprints as WbsItemFormModel['sprints'])
    },
    [onChange, validate]
  )
  const onChangeScheduledDate = useCallback(
    (value: WbsItemFormModel['scheduledDate']) => {
      onChange('scheduledDate', value)
      validate('scheduledDate', value)
    },
    [onChange, validate]
  )
  const onChangeActualDate = useCallback(
    (value: WbsItemFormModel['actualDate']) => {
      onChange('actualDate', value)
      validate('actualDate', value)
    },
    [onChange, validate]
  )
  const onChangeDescription = useCallback(
    (value: WbsItemFormModel['description']) => {
      onChange('description', value)
      validate('description', value)
    },
    [onChange, validate]
  )
  const onChangeStartIf = useCallback(
    (value: WbsItemFormModel['startIf']) => {
      onChange('startIf', value)
      validate('startIf', value)
    },
    [onChange, validate]
  )
  const onChangeCompleteIf = useCallback(
    (value: WbsItemFormModel['completeIf']) => {
      onChange('completeIf', value)
      validate('completeIf', value)
    },
    [onChange, validate]
  )
  const onChangeAdditionalPropertyValue = useCallback(
    (
      wbsItemAdditionalProperty: WbsItemAdditionalPropertyEntity,
      value: WbsItemAdditionalPropertyValueType
    ) => {
      _onChangeAdditionalPropertyValue(wbsItemAdditionalProperty, value)
      validate(wbsItemAdditionalProperty.uuid, value)
    },
    [_onChangeAdditionalPropertyValue, validate]
  )
  const modifiers = useMemo(() => {
    const formModelModifiers: FormModelModifiers = {
      displayName: onChangeDisplayName,
      status: onChangeStatus,
      substatus: onChangeSubStatus,
      tags: onChangeTags,
      team: onChangeTeam,
      accountable: onChangeAccountable,
      responsible: onChangeResponsible,
      assignee: onChangeAssignee,
      watchers: onChangeWatchers,
      difficulty: onChangeDifficulty,
      priority: onChangePriority,
      estimatedStoryPoint: onChangeEstimatedStoryPoint,
      estimatedHour: onChangeEstimatedHour,
      sprint: onChangeSprint,
      sprints: isTaskSprintRuleSingle
        ? onChangeSprintSingleFromSprints
        : onChangeSprints,
      scheduledDate: onChangeScheduledDate,
      actualDate: onChangeActualDate,
      description: onChangeDescription,
      startIf: onChangeStartIf,
      completeIf: onChangeCompleteIf,
    } as FormModelModifiers
    if (wbsItemAdditionalProperties) {
      wbsItemAdditionalProperties.groupHeaderLayouts.forEach(
        ({ propertyLayouts }) => {
          propertyLayouts.forEach(({ wbsItemAdditionalProperty }) => {
            formModelModifiers[wbsItemAdditionalProperty.uuid] = (
              value: WbsItemAdditionalPropertyValueType
            ) =>
              onChangeAdditionalPropertyValue(wbsItemAdditionalProperty, value)
          })
        }
      )
    }
    return {
      onChangeDisplayName,
      onChangeStatus,
      onChangeSubStatus,
      onChangeTags,
      onChangeTeam,
      onChangeAccountable,
      onChangeResponsible,
      onChangeAssignee,
      onChangeWatchers,
      onChangeDifficulty,
      onChangePriority,
      onChangeEstimatedStoryPoint,
      onChangeEstimatedHour,
      onChangeSprint,
      onChangeSprints,
      onChangeScheduledDate,
      onChangeActualDate,
      onChangeDescription,
      onChangeStartIf,
      onChangeCompleteIf,
      onChangeAdditionalPropertyValue,
      formModelModifiers,
    }
  }, [
    onChangeAccountable,
    onChangeActualDate,
    onChangeAssignee,
    onChangeDescription,
    onChangeStartIf,
    onChangeCompleteIf,
    onChangeDifficulty,
    onChangeDisplayName,
    onChangePriority,
    onChangeEstimatedStoryPoint,
    onChangeEstimatedHour,
    onChangeResponsible,
    onChangeScheduledDate,
    onChangeSprint,
    onChangeSprints,
    onChangeSprintSingleFromSprints,
    isTaskSprintRuleSingle,
    onChangeStatus,
    onChangeSubStatus,
    onChangeTags,
    onChangeTeam,
    onChangeWatchers,
    onChangeAdditionalPropertyValue,
    wbsItemAdditionalProperties,
  ])
  return modifiers
}
